import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BASE_SIZE, ARENA_SIZE } from '../../../utils/constants.js';
import { colored, vary, seededRandom } from '../../Style.js';
import { SCIFI, metal, glow, halo, hot } from './style.js';

const CURTAIN_HEIGHT = 2.2;

// A team's HQ compound: a plated landing deck ringed by a bright team-coloured
// perimeter and a low light curtain, holo-emitters at the corners, cargo and
// an antenna mast at the back.
export class Base {
  constructor(scene, position, color) {
    this.scene = scene;
    this.position = position;
    this.color = color;
    this.meshes = [];
    this.holograms = [];
    this.ownMaterials = [];
    this.rand = seededRandom(position.x > 0 ? 23 : 17);
    this.uniforms = { uTime: { value: 0 }, uColor: { value: new THREE.Color(color) } };
    this.create();
  }

  add(object) {
    this.scene.add(object);
    this.meshes.push(object);
    return object;
  }

  own(material) {
    this.ownMaterials.push(material);
    return material;
  }

  create() {
    // Calculate clipped base boundaries to stay within arena
    const arenaHalf = ARENA_SIZE / 2;
    const halfBaseWidth = BASE_SIZE / 2;
    const halfBaseDepth = (BASE_SIZE * 1.15) / 2; // 15% taller in Z direction

    const westEdge = Math.max(this.position.x - halfBaseWidth, -arenaHalf);
    const eastEdge = Math.min(this.position.x + halfBaseWidth, arenaHalf);
    const northEdge = Math.max(this.position.z - halfBaseDepth, -arenaHalf);
    const southEdge = Math.min(this.position.z + halfBaseDepth, arenaHalf);

    const actualWidth = eastEdge - westEdge;
    const actualDepth = southEdge - northEdge;
    const centerX = (westEdge + eastEdge) / 2;
    const centerZ = (northEdge + southEdge) / 2;

    // Which way is the back of the compound (towards the arena wall)
    const back = this.position.x < 0 ? -1 : 1;
    const rand = this.rand;
    const team = new THREE.Color(this.color);

    // Darker plated deck, tinted towards the team colour
    const deckColor = new THREE.Color(SCIFI.alloyDark).lerp(team, 0.12);
    const deck = new THREE.Mesh(new THREE.PlaneGeometry(actualWidth, actualDepth), metal(deckColor.getHex(), { roughness: 0.6, metalness: 0.3 }));
    deck.rotation.x = -Math.PI / 2;
    deck.position.set(centerX, 0.02, centerZ);
    deck.receiveShadow = true;
    this.add(deck);

    const parts = [];
    const lights = [];

    // Panel grid lines across the deck
    for (let x = westEdge + 2.5; x < eastEdge - 1; x += 5) {
      parts.push(colored(new THREE.BoxGeometry(0.08, 0.02, actualDepth - 1.2), SCIFI.alloyDeep, { x, y: 0.03, z: centerZ }));
    }
    for (let z = northEdge + 2.5; z < southEdge - 1; z += 5) {
      parts.push(colored(new THREE.BoxGeometry(actualWidth - 1.2, 0.02, 0.08), SCIFI.alloyDeep, { x: centerX, y: 0.03, z }));
    }

    // Perimeter: a raised kerb with a thick glowing strip in the team colour
    const edges = [
      { from: [westEdge, northEdge], to: [eastEdge, northEdge] },
      { from: [eastEdge, northEdge], to: [eastEdge, southEdge] },
      { from: [eastEdge, southEdge], to: [westEdge, southEdge] },
      { from: [westEdge, southEdge], to: [westEdge, northEdge] }
    ];
    const curtain = [];
    for (const { from, to } of edges) {
      const dx = to[0] - from[0];
      const dz = to[1] - from[1];
      const length = Math.hypot(dx, dz);
      const angle = Math.atan2(dx, dz);
      const mx = from[0] + dx / 2;
      const mz = from[1] + dz / 2;
      parts.push(colored(new THREE.BoxGeometry(0.9, 0.22, length + 0.9), SCIFI.alloyLight, { x: mx, y: 0.11, z: mz, ry: angle }));
      lights.push(colored(new THREE.BoxGeometry(0.55, 0.06, length + 0.5), team, { x: mx, y: 0.24, z: mz, ry: angle }));
      lights.push(colored(new THREE.BoxGeometry(0.12, 0.07, length + 0.5), hot(team, 0.6), { x: mx, y: 0.25, z: mz, ry: angle }));

      // Light curtain: a vertical strip fading upwards, animated in the shader
      const strip = new THREE.PlaneGeometry(length, CURTAIN_HEIGHT);
      strip.translate(0, CURTAIN_HEIGHT / 2 + 0.22, 0);
      strip.rotateY(angle + Math.PI / 2);
      strip.translate(mx, 0, mz);
      curtain.push(strip);
    }

    // Corner holo-emitters: a post with a spinning team hologram on top
    const corners = [
      [westEdge, northEdge], [eastEdge, northEdge],
      [westEdge, southEdge], [eastEdge, southEdge]
    ];
    const holoMaterial = this.own(new THREE.MeshBasicMaterial({
      color: hot(team, 0.2),
      transparent: true,
      opacity: 0.75,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    }));
    const holoGeometry = new THREE.OctahedronGeometry(0.55, 0);
    for (const [x, z] of corners) {
      parts.push(colored(new THREE.CylinderGeometry(0.55, 0.75, 0.5, 6), SCIFI.alloyDark, { x, y: 0.25, z }));
      parts.push(colored(new THREE.CylinderGeometry(0.2, 0.3, 2.6, 6), SCIFI.alloy, { x, y: 1.8, z }));
      parts.push(colored(new THREE.CylinderGeometry(0.6, 0.35, 0.3, 6), SCIFI.alloyLight, { x, y: 3.2, z }));
      lights.push(colored(new THREE.CylinderGeometry(0.45, 0.45, 0.06, 12), team, { x, y: 3.37, z }));
      lights.push(colored(new THREE.BoxGeometry(0.08, 1.8, 0.08), team, { x, y: 1.8, z: z + 0.27 }));

      const holo = new THREE.Mesh(holoGeometry, holoMaterial);
      holo.position.set(x, 4.3, z);
      holo.scale.set(1, 1.5, 1);
      holo.userData.phase = x * 0.1 + z * 0.3;
      this.add(holo);
      this.holograms.push(holo);
      // Projection cone from the emitter up to the hologram
      const cone = new THREE.Mesh(new THREE.ConeGeometry(0.75, 0.9, 12, 1, true), this.own(new THREE.MeshBasicMaterial({
        color: team, transparent: true, opacity: 0.18, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false
      })));
      cone.rotation.x = Math.PI;
      cone.position.set(x, 3.85, z);
      this.add(cone);
      const glowSprite = halo(team, 3.5, 0.5);
      glowSprite.position.set(x, 4.3, z);
      this.own(glowSprite.material);
      this.add(glowSprite);
    }

    // Antenna mast at the back with a dish and a blinking tip
    const mastX = centerX + back * (actualWidth / 2 - 2);
    const mastZ = centerZ;
    parts.push(colored(new THREE.BoxGeometry(2.2, 0.6, 2.2), SCIFI.alloyDark, { x: mastX, y: 0.3, z: mastZ }));
    parts.push(colored(new THREE.CylinderGeometry(0.18, 0.35, 9, 6), SCIFI.alloy, { x: mastX, y: 4.8, z: mastZ }));
    for (const y of [3.5, 6, 8]) {
      parts.push(colored(new THREE.BoxGeometry(1.6 - y * 0.1, 0.12, 0.12), SCIFI.alloyLight, { x: mastX, y, z: mastZ }));
    }
    const dish = new THREE.SphereGeometry(1.4, 12, 6, 0, Math.PI * 2, 0, Math.PI / 3);
    parts.push(colored(dish, SCIFI.trim, { x: mastX - back * 0.5, y: 6.2, z: mastZ, rz: back * 1.1 }));
    this.beacon = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), this.own(new THREE.MeshBasicMaterial({ color: 0xff3a3a, toneMapped: false })));
    this.beacon.position.set(mastX, 9.45, mastZ);
    this.add(this.beacon);

    // Cargo stacks in the back corners, clear of the fabricators
    for (const side of [-1, 1]) {
      const cx = centerX + back * (actualWidth / 2 - 2.2);
      const cz = centerZ + side * (actualDepth / 2 - 2.4);
      const body = SCIFI.containers[Math.floor(rand() * SCIFI.containers.length)];
      parts.push(colored(new THREE.BoxGeometry(2.4, 1.4, 1.6), vary(body, rand, 0.06), { x: cx, y: 0.7, z: cz }));
      parts.push(colored(new THREE.BoxGeometry(2.45, 0.12, 1.65), SCIFI.alloyDark, { x: cx, y: 1.35, z: cz }));
      parts.push(colored(new THREE.BoxGeometry(1.2, 0.9, 1.2), SCIFI.alloyLight, { x: cx - back * 0.3, y: 1.85, z: cz, ry: rand() * 0.5 }));
      lights.push(colored(new THREE.BoxGeometry(1.24, 0.06, 1.24), team, { x: cx - back * 0.3, y: 1.9, z: cz, ry: 0 }));
      parts.push(colored(new THREE.CylinderGeometry(0.35, 0.35, 1.0, 10), SCIFI.alloy, { x: cx - back * 1.8, y: 0.5, z: cz + side * 0.2 }));
    }

    // Base core: a hexagonal pad in the centre of the deck with the team emblem
    const core = new THREE.CylinderGeometry(3.2, 3.4, 0.08, 6);
    parts.push(colored(core, SCIFI.alloy, { x: centerX, y: 0.04, z: centerZ, ry: Math.PI / 6 }));
    lights.push(colored(new THREE.RingGeometry(2.7, 2.95, 6, 1), team, { rx: -Math.PI / 2, x: centerX, y: 0.09, z: centerZ, ry: Math.PI / 6 }));

    const props = new THREE.Mesh(mergeGeometries(parts), metal(0xffffff, { vertexColors: true }));
    props.castShadow = true;
    props.receiveShadow = true;
    this.add(props);

    this.add(new THREE.Mesh(mergeGeometries(lights), glow(0xffffff, { vertexColors: true, boost: 1.8 })));

    const curtainMaterial = this.own(new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */`
        varying vec2 vUv;
        varying vec3 vWorld;
        void main() {
          vUv = uv;
          vWorld = (modelMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * viewMatrix * vec4(vWorld, 1.0);
        }
      `,
      fragmentShader: /* glsl */`
        uniform float uTime;
        uniform vec3 uColor;
        varying vec2 vUv;
        varying vec3 vWorld;
        void main() {
          float y = vUv.y;
          float fade = pow(1.0 - y, 1.6);
          float bands = 0.75 + 0.25 * sin((vWorld.x + vWorld.z) * 1.2 - uTime * 3.0);
          float scan = smoothstep(0.85, 1.0, sin(y * 12.0 - uTime * 4.0) * 0.5 + 0.5);
          gl_FragColor = vec4(uColor * (fade * 0.95 * bands + scan * fade * 0.5), 1.0);
        }
      `
    }));
    const curtainMesh = new THREE.Mesh(mergeGeometries(curtain), curtainMaterial);
    curtainMesh.renderOrder = 2;
    this.add(curtainMesh);

    // One light per base, washing the deck in the team colour
    this.light = new THREE.PointLight(this.color, 60, 34, 1.6);
    this.light.position.set(centerX, 6, centerZ);
    this.add(this.light);
  }

  update(elapsed) {
    this.uniforms.uTime.value = elapsed;
    for (const holo of this.holograms) {
      holo.rotation.y = elapsed * 1.4 + holo.userData.phase;
      holo.position.y = 4.3 + Math.sin(elapsed * 2 + holo.userData.phase) * 0.12;
    }
    if (this.beacon) this.beacon.visible = (elapsed * 0.8) % 1 < 0.35;
    if (this.light) this.light.intensity = 55 + Math.sin(elapsed * 2.1) * 8;
  }

  remove() {
    const geometries = new Set();
    this.meshes.forEach(mesh => {
      this.scene.remove(mesh);
      mesh.traverse?.(child => {
        if (child.isMesh) geometries.add(child.geometry);
      });
    });
    geometries.forEach(geometry => geometry.dispose());
    this.ownMaterials.forEach(material => material.dispose());
    this.ownMaterials = [];
    this.meshes = [];
    this.holograms = [];
  }
}
